/**
 * 프로젝트 내보내기·불러오기 — 워크플로 JSON 내려받기/불러오기(CanvasToolbar)의 프로젝트판.
 *
 * 파일 하나에 프로젝트(이름·설명·시간대), 빈 폴더, 워크플로 전부(이름·설명·아이콘·폴더·**서버에 저장된 최신 캔버스**)를 싣는다.
 * 불러오기는 **항상 새 프로젝트**를 만든다 — 있는 프로젝트에 섞으면 무엇이 덮였는지 알 수 없다.
 * 이름이 겹치면 뒤에 (2), (3)… 을 붙인다.
 *
 * 데이터 노드는 정의의 값을 채운 채로 싣는다(loadCanvas 가 채운다). 다른 설치로 옮겨 그 정의가 없어도 노드 값이
 * 남고, 같은 정의가 있으면 저장할 때 다시 정의 id 만 남는다(definitionRefs.dehydrate 는 모르는 정의를 건드리지 않는다).
 *
 * 버전 2 (2026-10-03): 워크플로가 쓰는 **데이터 정의와 SAM유형**도 싣는다(samTypes · dataDefinitions). 불러올 때 서버가
 * 같은 것이 있으면 그대로 쓰고 없으면 만들어(POST /api/data-definitions/import), 노드의 dataDefinitionId 를 새 id 로 바꾼다.
 * 테라스트림 이관(services/migrate_terastream)도 이 모양으로 만든다 — 같은 불러오기를 지난다.
 */
import * as repo from './repo';
import { be1Fetch } from '../config/api';
import { migrateCanvas } from './canvasSchema';
import { loadEmptyFolders, normalizeFolder, saveEmptyFolders } from '../utils/workflowFolders';
import type { Project, Workflow } from '../store/project/projectSlice';
import i18n from '../i18n';

export const PROJECT_FILE_KIND = 'openflow.project';
export const PROJECT_FILE_VERSION = 2;

export interface ProjectFileWorkflow {
  name: string;
  description?: string;
  icon?: string;
  folder?: string;
  /** 이 파일 안에서의 이름표 — 다른 워크플로의 실행 노드가 config.workflowId 로 가리킨다(테라스트림 이관의 tsref:이름).
   *  불러올 때 새로 만든 워크플로 id 로 바뀐다 (remapWorkflowRefs) */
  ref?: string;
  /** 저장된 캔버스가 없던 워크플로(새 파일)는 null */
  canvas: { nodes: any[]; edges: any[]; settings?: any; schemaVersion?: number } | null;
}

/** 파일에 실린 SAM유형 — ref 는 이 파일 안에서의 이름표(내보낸 서버의 id 또는 이관 도구가 붙인 이름) */
export interface ProjectFileSamType {
  ref: string; name: string; description?: string;
  format: 'delimited' | 'fixed'; delimiter?: string; hasHeader?: boolean; encoding?: string; fieldWidths?: string;
  columns: Array<{ name: string; dataType?: string; [k: string]: any }>;
}

/** 파일에 실린 데이터 정의 — 노드의 dataDefinitionId 가 ref 를 가리킨다 */
export interface ProjectFileDefinition {
  ref: string; type: 'sam' | 'db'; name: string;
  source: Record<string, any> & { samTypeRef?: string };
  columns?: Array<{ name: string; dataType?: string; [k: string]: any }>;
}

export interface ProjectFile {
  kind: typeof PROJECT_FILE_KIND;
  version: number;
  exportedAt: string;
  project: { name: string; description?: string; timeZone?: string | null };
  emptyFolders?: string[];
  workflows: ProjectFileWorkflow[];
  samTypes?: ProjectFileSamType[];
  dataDefinitions?: ProjectFileDefinition[];
  /** 다른 도구에서 옮겨 온 파일이면 그 출처 (테라스트림 이관 등) */
  source?: Record<string, any>;
}

/** 파일 이름에 쓸 수 없는 문자만 거른다 */
export const fileNameOf = (name?: string) => (name || '').trim().replace(/[\\/:*?"<>|]+/g, '_') || 'project';

export function buildProjectFile(
  project: Pick<Project, 'name' | 'description'>, timeZone: string | null,
  emptyFolders: string[], workflows: ProjectFileWorkflow[], now = new Date(),
  layouts: { samTypes?: ProjectFileSamType[]; dataDefinitions?: ProjectFileDefinition[] } = {},
): ProjectFile {
  return {
    kind: PROJECT_FILE_KIND,
    version: PROJECT_FILE_VERSION,
    exportedAt: now.toISOString(),
    project: { name: project.name, description: project.description || '', timeZone: timeZone || null },
    emptyFolders,
    workflows,
    ...(layouts.samTypes?.length ? { samTypes: layouts.samTypes } : {}),
    ...(layouts.dataDefinitions?.length ? { dataDefinitions: layouts.dataDefinitions } : {}),
  };
}

/** 캔버스들이 가리키는 데이터 정의 id */
export function definitionIdsOf(canvases: Array<{ nodes?: any[] } | null>): Set<string> {
  const ids = new Set<string>();
  canvases.forEach((c) => (c?.nodes || []).forEach((n: any) => {
    const id = n?.data?.config?.dataDefinitionId;
    if (typeof id === 'string' && id) ids.add(id);
  }));
  return ids;
}

/** 노드의 dataDefinitionId 를 새 id 로 — 표에 없는 것은 그대로 둔다 */
export function remapDefinitionIds<T extends { nodes?: any[] }>(canvas: T, map: Record<string, string>): T {
  if (!canvas?.nodes || !Object.keys(map).length) return canvas;
  return {
    ...canvas,
    nodes: canvas.nodes.map((n: any) => {
      const id = n?.data?.config?.dataDefinitionId;
      return id && map[id] && map[id] !== id
        ? { ...n, data: { ...n.data, config: { ...n.data.config, dataDefinitionId: map[id] } } }
        : n;
    }),
  };
}

/** 이 서버의 정의·SAM유형 중 워크플로가 쓰는 것만 파일 모양으로 — 다른 서버로 옮겨도 정의가 따라간다 */
async function collectLayouts(ids: Set<string>): Promise<{ samTypes: ProjectFileSamType[]; dataDefinitions: ProjectFileDefinition[] }> {
  if (!ids.size) return { samTypes: [], dataDefinitions: [] };
  try {
    const [dr, tr] = await Promise.all([be1Fetch('/api/data-definitions/', undefined, { retries: 0 }), be1Fetch('/api/sam-types/', undefined, { retries: 0 })]);
    if (!dr.ok) return { samTypes: [], dataDefinitions: [] };
    const items: any[] = (await dr.json()).items || [];
    const st = tr.ok ? await tr.json() : { profiles: [], types: [] };
    const dataDefinitions: ProjectFileDefinition[] = [];
    const typeIds = new Set<string>();
    items.filter((i) => ids.has(i.id)).forEach((i) => {
      const { samTypeId, ...source } = i.source || {};
      if (samTypeId) typeIds.add(samTypeId);
      dataDefinitions.push({ ref: i.id, type: i.type === 'db' ? 'db' : 'sam', name: i.name,
        source: { ...source, ...(samTypeId ? { samTypeRef: samTypeId } : {}) }, columns: i.columns || [] });
    });
    const samTypes: ProjectFileSamType[] = (st.types || []).filter((x: any) => typeIds.has(x.id)).map((x: any) => {
      const p = (st.profiles || []).find((q: any) => q.id === x.profileId) || {};
      return { ref: x.id, name: x.name, description: x.description || '', format: p.format || 'delimited', delimiter: p.delimiter,
        hasHeader: !!p.hasHeader, encoding: p.encoding, fieldWidths: x.fieldWidths || '', columns: x.columns || [] };
    });
    return { samTypes, dataDefinitions };
  } catch {
    return { samTypes: [], dataDefinitions: [] };    // 정의를 못 실어도 노드에 값이 채워져 있어 워크플로는 옮겨진다
  }
}

/** 받은 JSON 이 프로젝트 파일인지 — 아니면 까닭을 담은 Error */
export function parseProjectFile(data: any): ProjectFile {
  if (!data || typeof data !== 'object' || data.kind !== PROJECT_FILE_KIND) {
    // 워크플로 파일을 잘못 고른 경우를 따로 알린다 — 가장 흔한 실수다
    throw new Error(i18n.t(data?.kind === 'openflow.canvas' ? 'layout:transfer.isCanvasFile' : 'layout:transfer.notProjectFile'));
  }
  if (Number(data.version) > PROJECT_FILE_VERSION) throw new Error(i18n.t('layout:transfer.tooNew', { version: data.version }));
  if (!data.project?.name || !Array.isArray(data.workflows)) throw new Error(i18n.t('layout:transfer.notProjectFile'));
  return data as ProjectFile;
}

/** 겹치지 않는 프로젝트 이름 — "이름", "이름 (2)", "이름 (3)" … */
export function uniqueProjectName(name: string, existing: string[]): string {
  const taken = new Set(existing.map((n) => n.trim()));
  if (!taken.has(name.trim())) return name.trim();
  for (let i = 2; ; i += 1) {
    const next = `${name.trim()} (${i})`;
    if (!taken.has(next)) return next;
  }
}

async function readTimeZone(projectId: string): Promise<string | null> {
  try {
    const r = await be1Fetch(`/api/projects/${encodeURIComponent(projectId)}/settings`, undefined, { retries: 0 });
    if (!r.ok) return null;
    const body = await r.json();
    return body.timeZone ?? null;
  } catch { return null; }
}

/** 프로젝트를 파일 내용으로 — 서버에 저장된 최신 버전을 읽는다(저장하지 않은 편집은 싣지 않는다) */
export async function exportProject(project: Project, onProgress?: (done: number, total: number) => void): Promise<ProjectFile> {
  const workflows: Workflow[] = await repo.listWorkflows(project.id);
  const out: ProjectFileWorkflow[] = [];
  for (let i = 0; i < workflows.length; i += 1) {
    const w = workflows[i];
    const doc = w.curVerNo ? await repo.loadCanvas(w.id, w.curVerNo) : null;
    if (w.curVerNo && !doc) throw new Error(i18n.t('layout:transfer.readFailed', { name: w.name, ver: w.curVerNo }));
    out.push({
      name: w.name, description: w.description || '', icon: w.icon, folder: w.folder || '',
      canvas: doc ? { nodes: doc.nodes, edges: doc.edges, settings: doc.settings, schemaVersion: doc.schemaVersion } : null,
    });
    onProgress?.(i + 1, workflows.length);
  }
  const layouts = await collectLayouts(definitionIdsOf(out.map((w) => w.canvas)));
  return buildProjectFile(project, await readTimeZone(project.id), loadEmptyFolders(project.id), out, new Date(), layouts);
}

export interface ImportResult {
  project: Project;
  created: number;
  /** 만들지 못한 워크플로와 까닭 — 나머지는 계속 만든다 */
  failed: Array<{ name: string; message: string }>;
}

/** 불러오기 옵션 — 이관(테라스트림)에서 저장 폴더를 고를 때 */
export interface ImportOptions {
  /** 워크플로를 둘 폴더 — 주면 파일에 적힌 폴더 대신 이것(하위 경로 a/b 가능, 없는 폴더면 새로 생긴다) */
  folder?: string;
}

/** 같은 폴더 안에서 겹치지 않는 워크플로 이름 — "이름", "이름 (2)" … (있는 프로젝트에 넣을 때) */
export function uniqueWorkflowName(name: string, folder: string, taken: Set<string>): string {
  const key = (n: string) => `${normalizeFolder(folder)}\u0000${n.trim()}`;
  let out = name.trim();
  for (let i = 2; taken.has(key(out)); i += 1) out = `${name.trim()} (${i})`;
  taken.add(key(out));
  return out;
}

/** 정의·SAM유형을 먼저 — 노드가 가리킬 새 id 를 받는다. 실패해도 노드에 값이 채워져 있어 워크플로는 만든다 */
async function importLayouts(file: ProjectFile, defsFolder: string): Promise<Record<string, string>> {
  if (!file.samTypes?.length && !file.dataDefinitions?.length) return {};
  try {
    const r = await be1Fetch('/api/data-definitions/import', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ folder: defsFolder, samTypes: file.samTypes || [], definitions: file.dataDefinitions || [] }),
    }, { retries: 0 });
    return r.ok ? ((await r.json()).definitions || {}) : {};
  } catch { return {}; }
}

/**
 * 실행 노드가 가리키는 워크플로를 새 id 로 — 파일 안 이름표(ref)로 가리킨 것만 바꾼다.
 * 표에 없는 이관 자리표(tsref:·tsmissing:)는 비운다 — 배포·실행 때 「실행할 워크플로를 고르세요」 로 드러난다.
 */
export function remapWorkflowRefs<T extends { nodes?: any[] }>(canvas: T, map: Record<string, string>): T {
  if (!canvas?.nodes) return canvas;
  return {
    ...canvas,
    nodes: canvas.nodes.map((n: any) => {
      const id = n?.type === 'runWorkflow' ? n?.data?.config?.workflowId : undefined;
      if (typeof id !== 'string' || !id) return n;
      const next = map[id] ?? (/^ts(ref|missing):/.test(id) ? '' : id);
      return next === id ? n : { ...n, data: { ...n.data, config: { ...n.data.config, workflowId: next } } };
    }),
  };
}

/** 고른 폴더 아래에 파일이 정한 폴더 — 고른 폴더가 없으면 파일의 것 그대로 */
const joinFolder = (base: string | undefined, own: string | undefined) =>
  normalizeFolder(base === undefined ? (own || '') : [base, own || ''].filter(Boolean).join('/'));

/**
 * 파일의 워크플로들을 프로젝트에 만든다 — 새 프로젝트·있는 프로젝트가 같이 쓴다.
 * 두 번에 나눈다: 먼저 워크플로를 모두 만들어 id 를 받고, 그다음 캔버스를 저장한다 — 실행 노드가 같은 파일의 다른
 * 워크플로를 가리키면(테라스트림 exec_prj_block) 그 id 를 저장 전에 알아야 하기 때문이다.
 */
async function createWorkflows(
  file: ProjectFile, projectId: string, usrid: string, defMap: Record<string, string>, opts: ImportOptions,
  taken: Set<string> | null, onProgress?: (done: number, total: number) => void,
): Promise<{ created: number; failed: ImportResult['failed'] }> {
  let created = 0;
  const failed: ImportResult['failed'] = [];
  const made: Array<{ id: string; w: ProjectFileWorkflow } | null> = [];
  const refMap: Record<string, string> = {};
  for (const w of file.workflows) {
    try {
      if (w.canvas) {
        const m = migrateCanvas(w.canvas as any);
        if (m.tooNew) throw new Error(i18n.t('layout:transfer.canvasTooNew', { version: m.docVersion }));
      }
      const folder = joinFolder(opts.folder, w.folder);
      const name = taken ? uniqueWorkflowName(w.name, folder, taken) : w.name;
      const wf = await repo.createWorkflow({ projectId, name, description: w.description || '', icon: w.icon, folder });
      if (w.ref) refMap[w.ref] = wf.id;
      made.push({ id: wf.id, w });
    } catch (e) {
      failed.push({ name: w.name, message: (e as Error)?.message || String(e) });
      made.push(null);
    }
  }
  for (let i = 0; i < made.length; i += 1) {
    const it = made[i];
    if (it) {
      try {
        const c = it.w.canvas;
        if (c && (c.nodes?.length || c.edges?.length)) {
          const m = migrateCanvas(remapWorkflowRefs(remapDefinitionIds(c as any, defMap), refMap));
          await repo.saveCanvas(it.id, 0, { nodes: m.nodes, edges: m.edges, settings: m.settings }, usrid);
        }
        created += 1;
      } catch (e) {
        failed.push({ name: it.w.name, message: (e as Error)?.message || String(e) });
      }
    }
    onProgress?.(i + 1, file.workflows.length);
  }
  return { created, failed };
}

/** 파일로 새 프로젝트를 만든다 */
export async function importProject(
  file: ProjectFile, existingProjectNames: string[], usrid: string,
  onProgress?: (done: number, total: number) => void, opts: ImportOptions = {},
): Promise<ImportResult> {
  const name = uniqueProjectName(file.project.name, existingProjectNames);
  const project = await repo.createProject({ name, description: file.project.description || '' }, usrid);
  if (file.project.timeZone) {
    await be1Fetch(`/api/projects/${encodeURIComponent(project.id)}/settings`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ timeZone: file.project.timeZone }),
    }, { retries: 0 }).catch(() => undefined);   // 시간대는 나중에 프로젝트 설정에서 다시 정할 수 있다
  }
  if (file.emptyFolders?.length && opts.folder === undefined) saveEmptyFolders(project.id, file.emptyFolders);
  const defMap = await importLayouts(file, name);
  const r = await createWorkflows(file, project.id, usrid, defMap, opts, null, onProgress);
  return { project, ...r };
}

/**
 * 있는 프로젝트에 넣는다 — 테라스트림 이관에서 「지금 프로젝트」 를 고른 경우.
 * 같은 폴더에 같은 이름이 있으면 " (2)" 를 붙인다(덮어쓰지 않는다). 정의는 sam/<프로젝트 이름>/<폴더> 아래에 만든다.
 */
export async function importIntoProject(
  file: ProjectFile, project: Pick<Project, 'id' | 'name'>, usrid: string, opts: ImportOptions,
  onProgress?: (done: number, total: number) => void,
): Promise<ImportResult> {
  const existing: Workflow[] = await repo.listWorkflows(project.id);
  const taken = new Set(existing.map((w) => `${normalizeFolder(w.folder || '')}\u0000${w.name.trim()}`));
  const defMap = await importLayouts(file, [project.name, normalizeFolder(opts.folder || '')].filter(Boolean).join('/'));
  const r = await createWorkflows(file, project.id, usrid, defMap, opts, taken, onProgress);
  return { project: project as Project, ...r };
}

/** 브라우저에서 JSON 파일로 내려받는다 */
export function downloadJson(data: unknown, fileName: string): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  downloadBlob(blob, fileName);
}

export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}
